import { AlertTriangle } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";

// Deal Pipeline spec §6.5 — modifications to LegalEntityName, EIN, SSN,
// banking, DateOfBirth on a counterparty linked to an active Deal must be
// confirmed. The counterparty Detail response carries no active-deal flag
// (the API checks active-deal links only on archive), so the modal fires
// unconditionally on critical-field changes, which is the safer default.

export interface CriticalFieldChange {
  /** Display label of the field that changed (e.g. "SSN", "EIN"). */
  field: string;
  /** What it looked like before. May be a masked value for sensitive
   *  fields — never show cleartext, even in the diff. */
  before?: string | null;
  /** What it will become after the save. Same masking rules. */
  after?: string | null;
}

export interface CriticalFieldEditConfirmModalProps {
  open: boolean;
  changes: CriticalFieldChange[];
  onConfirm: () => void;
  onCancel: () => void;
}

export function CriticalFieldEditConfirmModal({
  open,
  changes,
  onConfirm,
  onCancel,
}: CriticalFieldEditConfirmModalProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Confirm critical field changes"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <div className="flex items-start gap-space-2 text-small text-status-error">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden className="mt-[2px]" />
          <p className="m-0">
            You're changing fields that are critical to identity verification, tax filing, or banking.
            On a counterparty linked to an active deal, this can affect closing documents and
            disbursement instructions. The change will be written to the audit log.
          </p>
        </div>

        <ul className="flex flex-col gap-space-2 border border-border-light rounded-md p-space-3 bg-white-warm">
          {changes.map((c) => (
            <li key={c.field} className="text-caption">
              <div className="font-display font-semibold uppercase tracking-wide text-onyx">
                {c.field}
              </div>
              <div className="font-mono text-gray-mid mt-space-1">
                <span aria-label="before">{c.before ?? "—"}</span>
                <span aria-hidden> → </span>
                <span aria-label="after" className="text-onyx">{c.after ?? "—"}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
